class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.bindDrawerInteractions();
  }

  bindDrawerInteractions() {
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.bindRemoveButtons();
  }

  bindRemoveButtons() {
    if (this.removeDelegateBound) return;
    this.removeDelegateBound = true;

    this.addEventListener('click', (event) => {
      const removeButton = event.target.closest('cart-remove-button');
      if (!removeButton || event.brigidCartRemoveHandled) return;
      const cartItems = this.querySelector('cart-drawer-items');
      if (!cartItems || typeof cartItems.updateQuantity !== 'function') return;
      event.preventDefault();
      event.brigidCartRemoveHandled = true;
      cartItems.updateQuantity(removeButton.dataset.index, 0, event);
    });
  }

  setHeaderCartIconAccessibility() {
    if (CartDrawer.triggersBound) {
      this.refreshTriggerAccessibility();
      return;
    }
    CartDrawer.triggersBound = true;

    document.addEventListener('click', (event) => {
      const cartLink = event.target.closest('[data-cart-drawer-trigger]');
      if (!cartLink) return;
      event.preventDefault();
      document.querySelector('[data-bh-drawer]')?.classList.remove('is-open');
      document.querySelector('account-drawer')?.close();
      const drawer = document.querySelector('cart-drawer');
      drawer?.open(cartLink);
    });

    document.addEventListener('keydown', (event) => {
      const cartLink = event.target.closest('[data-cart-drawer-trigger]');
      if (!cartLink || event.code.toUpperCase() !== 'SPACE') return;
      event.preventDefault();
      document.querySelector('cart-drawer')?.open(cartLink);
    });

    this.refreshTriggerAccessibility();
  }

  refreshTriggerAccessibility() {
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.querySelector('.drawer__inner') || document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const html = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (html == null) return;
      sectionElement.innerHTML = html;
    });

    this.syncBrigidHeaderCount(parsedState);
    setTimeout(() => {
      this.bindDrawerInteractions();
      this.open();
    });
  }

  syncBrigidHeaderCount(parsedState) {
    const countFromState = Number(parsedState?.item_count);
    const countFromBubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
    const count = Number.isFinite(countFromState)
      ? countFromState
      : countFromBubble
        ? parseInt(countFromBubble.textContent, 10) || 0
        : this.querySelectorAll('.cart-item').length;

    const wrap = document.querySelector('[data-brigid-cart-badge-host]') || document.querySelector('.bh-cart-icon-wrap');
    if (wrap) {
      let badge = wrap.querySelector('.bh-count');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'bh-count bh-count--floating';
          wrap.appendChild(badge);
        }
        badge.textContent = String(count);
      } else if (badge) {
        badge.remove();
      }
    }

    const menuLink = document.querySelector('[data-brigid-cart-menu]') || document.querySelector('.bh-group-link[data-cart-drawer-trigger]');
    if (menuLink) {
      const label = menuLink.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
      menuLink.textContent = `${label} (${count})`;
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

if (typeof CartItems === 'function' && !customElements.get('cart-drawer-items')) {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    if (event?.cartData) drawer.syncBrigidHeaderCount(event.cartData);
  });
}
